import React from 'react';
import { useLocation } from 'react-router';
import Btn from '../../../../components/molecules/Btn/Btn';
import { BtnModifier } from '../../../../components/molecules/Btn';
import { Icons } from '../../../../components/atoms/SVG';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { ContextMenu, ContextMenuButton } from '../../ContextMenu';
import { ButtonType } from '../../../../types/ButtonType';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { PostFormTypes, PostTypes } from '../Post.types';
import { useRefreshBlock } from '../../../BlockFetcher';
import { SonForm } from '../../../Form2';
import { ContextMenuLink } from '../../ContextMenu/partials/ContextMenuLink';

type PostInteractions = {
  postId: string;
  replyForm?: SonForm<PostFormTypes>;
  updateForm?: SonForm<PostFormTypes>;
  deleteForm?: SonForm<any>;
  currentForm: SonForm<PostFormTypes> | undefined;
  setCurrentForm: (newState: SonForm<PostFormTypes> | undefined) => void;
  type: PostTypes;
  editEditorId: string;
  replyEditorId?: string;
  postNumber: number | string,
};

export function PostsInteractions({
  postId,
  replyForm,
  updateForm,
  deleteForm,
  setCurrentForm,
  currentForm,
  type,
  editEditorId,
  replyEditorId,
  postNumber,
}: PostInteractions) {
  const { t } = useSonTranslation('Posts');
  const { hash } = useLocation();
  const { refresh } = useRefreshBlock();

  const handleReplyClick = () => {
    if (currentForm === replyForm) {
      setCurrentForm(undefined);
    } else {
      setCurrentForm(replyForm);
    }
  };

  return (
    <div className="posts__interactions">
      {(replyForm || updateForm || deleteForm) && (
        <>
          {replyEditorId && replyForm && type !== 'reply' && (
            <Btn
              id={`reply-button-${postId}`}
              href={`#reply-editor-${postId}`}
              ariaDisabled={hash.includes(replyEditorId) && currentForm && currentForm === replyForm}
              disabledLink={hash.includes(replyEditorId) && currentForm && currentForm === replyForm}
              modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
              svgSpriteId={Icons.REPLY_MESSAGE}
              text={t(`default.replyButton.${type}`, { postNumber })}
              titleText={t(`default.replyButton.${type}`, { postNumber })}
              hiddenText
              tooltipPlacement={TooltipPlacement.TOP}
              onClick={() => handleReplyClick()}
            />
          )}
          {((!deleteForm && updateForm && currentForm !== updateForm) ||
            (deleteForm && updateForm && currentForm !== updateForm) ||
            (deleteForm && !updateForm)) && (
            <ContextMenu
              tooltipText={t(`default.contextMenu.tooltip.title.${type}`, { postNumber })}
              tooltipPlacement={TooltipPlacement.TOP}
            >
              {updateForm && (
                <ContextMenuLink
                  href={`#edit-editor-${postId}`}
                  routing={false}
                  onClick={() => setCurrentForm(updateForm)}
                >
                  {t(`default.contextMenu.edit.title.${type}`)}
                </ContextMenuLink>
              )}
              {deleteForm && (
                <ContextMenuButton aria-haspopup onClick={() => setCurrentForm(deleteForm)}>
                  {t(`default.contextMenu.delete.${type}.title`)}
                </ContextMenuButton>
              )}
            </ContextMenu>
          )}
          {hash.includes(editEditorId) && currentForm === updateForm && (
            <Btn
              href={`#post-heading-${postId}`}
              modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT, BtnModifier.ACTIVE]}
              onClick={() => {
                setCurrentForm(undefined);

                // only threads contain attachedFiles, which are connected to WS in edit mode.
                // default mode will not receive updates -> manually refresh when closing.
                // see SIBZIEL-13036
                if (type === 'thread') {
                  refresh();
                }
              }}
              tooltipPlacement={TooltipPlacement.TOP}
              type={ButtonType.BUTTON}
              svgSpriteId={Icons.DECLINE_CROSS}
              text={t('default.stopEditing')}
              titleText={t('default.stopEditing')}
              hiddenText
            />
          )}
        </>
      )}
    </div>
  );
}
